import { AutoDimensionType } from "building-types/autoDimensions/autoDimensionTypesEnum";
import type { AutoDimensionHandle } from "building-types/handles/docEntityHandleTypes";
import { DocEntityPrefix } from "building-types/handles/docEntityPrefix";
import { makeSettingsCriteriaHash } from "building-types/provenance/settingsCriteriaTools";
import { symmetricInteriorRoomEdgeDimensionHash } from "../generate/getEdgeMatches";

export function autoDimensionHandleHashNew(e: AutoDimensionHandle) {
  const provenance = e.provenance;
  if (!provenance) {
    return "";
  }
  const conditionHash = makeSettingsCriteriaHash(provenance.criteria);

  switch (provenance.type) {
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_SINGLE: {
      if (!provenance?.roomEdge) {
        return "";
      }
      /**
       * Both AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_SINGLE and AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_MULTIPLE
       * are meant to produce the same hash if the first room edge is the same.
       */
      return `${DocEntityPrefix.AutoDimension}.${conditionHash}.${AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_MULTIPLE}.${provenance.roomEdge.roomId}.${provenance.roomEdge.edgeId}.${provenance.rowLevel}.${provenance.isOrthogonal}`;
    }
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_MULTIPLE: {
      if (!provenance?.roomEdges?.length) {
        return "";
      }
      return `${DocEntityPrefix.AutoDimension}.${conditionHash}.${provenance.type}.${provenance.roomEdges[0].roomId}.${provenance.roomEdges[0].edgeId}.${provenance.rowLevel}.${provenance.isOrthogonal}`;
    }
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_SINGLE: {
      if (!provenance?.roomEdge) {
        return "";
      }
      /**
       * Both AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_SINGLE and AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_MULTIPLE
       * are meant to produce the same hash if the first room edge is the same.
       */
      return `${DocEntityPrefix.AutoDimension}.${conditionHash}.${AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_MULTIPLE}.${provenance.roomEdge.roomId}.${provenance.roomEdge.edgeId}.${provenance.roomEdge.entityLocation}.${provenance.rowLevel}.${provenance.isOrthogonal}`;
    }
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_MULTIPLE: {
      if (!provenance?.roomEdges?.length) {
        return "";
      }
      return `${DocEntityPrefix.AutoDimension}.${conditionHash}.${provenance.type}.${provenance.roomEdges[0].roomId}.${provenance.roomEdges[0].edgeId}.${provenance.roomEdges[0].entityLocation}.${provenance.rowLevel}.${provenance.isOrthogonal}`;
    }
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ORIENTATION:
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ORIENTATION:
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ORIENTATION:
      return `${DocEntityPrefix.AutoDimension}.${conditionHash}.${provenance.type}.${provenance.levelId}${provenance.orientation}.${provenance.rowLevel}`;
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_MULTIPLE: {
      if (!provenance?.roomEdges?.length) {
        return "";
      }
      return `${DocEntityPrefix.AutoDimension}.${conditionHash}.${provenance.type}.${provenance.roomEdges[0].roomId}.${provenance.roomEdges[0].edgeId}.${provenance.roomEdges[0].entityLocation}.${provenance.rowLevel}.${provenance.isOrthogonal}`;
    }
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_SINGLE: {
      if (!provenance?.roomEdge) {
        return "";
      }
      /**
       * Both AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_SINGLE and AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_MULTIPLE
       * are meant to produce the same hash if the first room edge is the same.
       */
      return `${DocEntityPrefix.AutoDimension}.${conditionHash}.${AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_MULTIPLE}.${provenance.roomEdge.roomId}.${provenance.roomEdge.edgeId}.${provenance.roomEdge.entityLocation}.${provenance.rowLevel}.${provenance.isOrthogonal}`;
    }
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_OPPOSITE_ROOM_EDGE_PAIR: {
      if (!provenance?.roomEdgePairs?.length) {
        return "";
      }
      const interiorRoomEdgeDimHash = symmetricInteriorRoomEdgeDimensionHash([
        provenance.roomEdgePairs[0][0],
        provenance.roomEdgePairs[0][1],
      ]);
      return `${DocEntityPrefix.AutoDimension}.${conditionHash}.${provenance.type}.${interiorRoomEdgeDimHash}`;
    }
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_ORTHOGONAL_ROOM_EDGE: {
      if (!provenance?.roomEdges) {
        return "";
      }

      const edge = provenance.roomEdges[0];

      if (!edge) {
        return "";
      }

      const { roomId, edgeId } = edge;

      return `${DocEntityPrefix.AutoDimension}.${conditionHash}.${provenance.type}.${roomId}.${edgeId}`;
    }
  }
}
